import { ColumnDef } from '@tanstack/react-table'
import { IntegrationVendor } from '@magnetar/data-access'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Edit, Power, PowerOff } from 'lucide-react'
import { integrationVendorService } from '@/services/integration_vendor_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

type IntegrationVendorTableMeta = {
  reloadData?: () => void
  onEdit?: (vendor: IntegrationVendor) => void
}

export const columns: ColumnDef<IntegrationVendor>[] = [
  {
    accessorKey: 'integration_vendor_id',
    header: 'ID',
    cell: ({ row }) => {
      const id: number = row.getValue('integration_vendor_id')
      return (
        <Badge variant="outline" className="font-medium">
          {id}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'vendor_name',
    header: '廠商名稱',
  },
  {
    accessorKey: 'app_id',
    header: 'App ID',
    cell: ({ row }) => {
      const appId: string = row.getValue('app_id')
      return (
        <Badge variant="secondary" className="font-mono">
          {appId}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'referer_urls',
    header: '允許來源',
    cell: ({ row }) => {
      const urls: string[] = row.getValue('referer_urls')
      if (!urls || urls.length === 0) {
        return <span className="text-muted-foreground">無限制</span>
      }
      return (
        <div className="flex flex-wrap gap-1">
          {urls.slice(0, 2).map((url, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {url}
            </Badge>
          ))}
          {urls.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{urls.length - 2}
            </Badge>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'permission_scopes',
    header: '權限範圍',
    cell: ({ row }) => {
      const scopes: string[] = row.getValue('permission_scopes')
      if (!scopes || scopes.length === 0) {
        return <span className="text-muted-foreground">無</span>
      }
      return (
        <div className="flex flex-wrap gap-1">
          {scopes.slice(0, 2).map((scope, index) => (
            <Badge key={index} variant="default" className="text-xs">
              {scope}
            </Badge>
          ))}
          {scopes.length > 2 && (
            <Badge variant="default" className="text-xs">
              +{scopes.length - 2}
            </Badge>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'is_active',
    header: '狀態',
    cell: ({ row }) => {
      const is_active: boolean = row.getValue('is_active')
      return (
        <Badge variant={is_active ? 'default' : 'secondary'}>{is_active ? '啟用' : '停用'}</Badge>
      )
    },
  },
  {
    accessorKey: 'created_at',
    header: '建立時間',
    cell: ({ row }) => {
      const date: string = row.getValue('created_at')
      return new Date(date).toLocaleDateString('zh-TW')
    },
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const vendor = row.original
      const { token } = useAuth()

      const handleEdit = () => {
        const meta = table.options.meta as IntegrationVendorTableMeta
        meta?.onEdit?.(vendor)
      }

      const handleDeactivate = async () => {
        if (!token) return

        const result = await integrationVendorService.deactivateVendor(
          vendor.integration_vendor_id,
          token,
        )

        if (result.success) {
          toast.success('廠商已停用')
          // Reload data
          const meta = table.options.meta as IntegrationVendorTableMeta
          meta?.reloadData?.()
        } else {
          toast.error('停用失敗')
        }
      }

      return (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleEdit}>
            <Edit className="h-4 w-4 mr-1" />
            編輯
          </Button>
          {vendor.is_active ? (
            <Button variant="outline" size="sm" onClick={handleDeactivate}>
              <PowerOff className="h-4 w-4 mr-1" />
              停用
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled>
              <Power className="h-4 w-4 mr-1" />
              已停用
            </Button>
          )}
        </div>
      )
    },
  },
]
